/**
 * FileIcon - Componente per icone file basate su estensione
 */

import {
  File,
  FileText,
  FileSpreadsheet,
  FileImage,
  FileVideo,
  FileAudio,
  FileArchive,
  FileCode,
  Folder,
  FolderOpen,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface FileIconProps {
  name: string;
  kind: 'file' | 'directory';
  isOpen?: boolean;
  className?: string;
  size?: number;
}

// Mapping estensioni a categorie
const extensionCategories: Record<string, string> = {
  // Documenti PDF
  pdf: 'pdf',
  // Documenti Word
  doc: 'word',
  docx: 'word',
  odt: 'word',
  rtf: 'word',
  // Fogli di calcolo
  xls: 'excel',
  xlsx: 'excel',
  ods: 'excel',
  csv: 'excel',
  // Presentazioni
  ppt: 'powerpoint',
  pptx: 'powerpoint',
  odp: 'powerpoint',
  // Immagini
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
  svg: 'image',
  bmp: 'image',
  ico: 'image',
  tiff: 'image',
  tif: 'image',
  // Video
  mp4: 'video',
  avi: 'video',
  mkv: 'video',
  mov: 'video',
  wmv: 'video',
  webm: 'video',
  // Audio
  mp3: 'audio',
  wav: 'audio',
  ogg: 'audio',
  flac: 'audio',
  aac: 'audio',
  wma: 'audio',
  // Archivi
  zip: 'archive',
  rar: 'archive',
  '7z': 'archive',
  tar: 'archive',
  gz: 'archive',
  bz2: 'archive',
  // Codice
  js: 'code',
  ts: 'code',
  jsx: 'code',
  tsx: 'code',
  html: 'code',
  css: 'code',
  json: 'code',
  xml: 'code',
  py: 'code',
  java: 'code',
  c: 'code',
  cpp: 'code',
  h: 'code',
  hpp: 'code',
  // Testo
  txt: 'text',
  md: 'text',
  log: 'text',
};

// Colori per categoria
const categoryColors: Record<string, string> = {
  pdf: 'text-red-500',
  word: 'text-blue-600',
  excel: 'text-green-600',
  powerpoint: 'text-orange-500',
  image: 'text-purple-500',
  video: 'text-pink-500',
  audio: 'text-cyan-500',
  archive: 'text-yellow-600',
  code: 'text-gray-600',
  text: 'text-gray-500',
  folder: 'text-amber-500',
  default: 'text-gray-400',
};

export function FileIcon({ name, kind, isOpen = false, className, size = 20 }: FileIconProps) {
  if (kind === 'directory') {
    const Icon = isOpen ? FolderOpen : Folder;
    return <Icon className={cn(categoryColors.folder, className)} size={size} />;
  }

  const extension = name.split('.').pop()?.toLowerCase() || '';
  const category = extensionCategories[extension] || 'default';
  const colorClass = categoryColors[category] || categoryColors.default;

  const IconComponent = (() => {
    switch (category) {
      case 'pdf':
      case 'word':
      case 'text':
        return FileText;
      case 'excel':
        return FileSpreadsheet;
      case 'image':
        return FileImage;
      case 'video':
        return FileVideo;
      case 'audio':
        return FileAudio;
      case 'archive':
        return FileArchive;
      case 'code':
        return FileCode;
      default:
        return File;
    }
  })();

  return <IconComponent className={cn(colorClass, className)} size={size} />;
}

// Helper per ottenere il tipo MIME da estensione
export function getMimeType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const mimeTypes: Record<string, string> = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    mp4: 'video/mp4',
    webm: 'video/webm',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    txt: 'text/plain',
    html: 'text/html',
    css: 'text/css',
    js: 'text/javascript',
    json: 'application/json',
    xml: 'application/xml',
    zip: 'application/zip',
  };
  return mimeTypes[ext] || 'application/octet-stream';
}

// Helper per verificare se un file è visualizzabile in preview
export function isPreviewable(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const previewableExtensions = [
    'pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg',
    'txt', 'md', 'log', 'json', 'xml', 'html', 'css', 'js',
  ];
  return previewableExtensions.includes(ext);
}

export default FileIcon;
